import { useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { Search, Gift, Crown, X, ChevronLeft, ChevronRight as ChevronRightIcon } from 'lucide-react'
import { useAppStore } from '../../store/useAppStore'
import { adminApi } from './adminApi'

interface UserRow {
  id: string
  line_uid: string
  display_name: string | null
  avatar_url: string | null
  role: 'user' | 'admin' | 'super_admin'
  dream_used_today: string | null
  created_at: string
  unlocked_gurus: { expires_at: string }[]
}

interface UsersResponse {
  users: UserRow[]
  total: number
  limit: number
  offset: number
}

const PAGE_SIZE = 20

function formatDate(s: string): string {
  const d = new Date(s)
  const months = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.']
  return `${d.getDate()} ${months[d.getMonth()]} ${(d.getFullYear() + 543).toString().slice(-2)}`
}

export default function AdminUsersPage() {
  const { lineUid } = useAppStore()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null)
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', lineUid, search, page],
    queryFn: () => adminApi.users(lineUid!, { search, limit: PAGE_SIZE, offset: page * PAGE_SIZE }) as Promise<UsersResponse>,
    enabled: !!lineUid,
  })

  const unlockMutation = useMutation({
    mutationFn: ({ userId, count, reason }: { userId: string; count: number; reason?: string }) =>
      adminApi.unlock(lineUid!, userId, count, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      setSelectedUser(null)
    },
  })

  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div className="p-4 md:p-6 lg:p-8">

      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-1" style={{ color: '#2d2820' }}>
          👥 ผู้ใช้งาน
        </h1>
        <p className="text-sm" style={{ color: '#6b6354' }}>
          จัดการผู้ใช้ ปลดล็อกสิทธิ์ และดูประวัติ
        </p>
      </div>

      {/* Search */}
      <div className="mb-4 relative">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2"
                style={{ color: '#8a8270' }} />
        <input
          type="text"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0) }}
          placeholder="ค้นหา line_uid หรือชื่อ..."
          className="w-full pl-11 pr-4 py-3 rounded-2xl text-sm outline-none"
          style={{
            background: '#faf7ef',
            border: '1px solid rgba(0,0,0,0.06)',
            color: '#2d2820',
          }}
        />
      </div>

      {/* Users Table */}
      <div className="rounded-2xl overflow-hidden"
           style={{ background: '#faf7ef', border: '1px solid rgba(0,0,0,0.04)' }}>
        {isLoading && (
          <div className="p-8 text-center text-sm" style={{ color: '#8a8270' }}>กำลังโหลด...</div>
        )}

        {data?.users && data.users.length === 0 && (
          <div className="p-8 text-center text-sm" style={{ color: '#8a8270' }}>ไม่พบผู้ใช้</div>
        )}

        {data?.users.map(u => (
          <UserCard key={u.id} user={u} onClick={() => setSelectedUser(u)} />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-2">
          <span className="text-xs" style={{ color: '#6b6354' }}>
            แสดง {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} จาก {total} คน
          </span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1 disabled:opacity-30"
                    style={{ background: '#faf7ef', color: '#2d2820' }}>
              <ChevronLeft size={14} /> ก่อน
            </button>
            <button onClick={() => setPage(p => p + 1)}
                    disabled={page >= totalPages - 1}
                    className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1 disabled:opacity-30"
                    style={{ background: '#faf7ef', color: '#2d2820' }}>
              ถัดไป <ChevronRightIcon size={14} />
            </button>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onUnlock={(count, reason) => unlockMutation.mutate({ userId: selectedUser.id, count, reason })}
          unlocking={unlockMutation.isPending}
        />
      )}
    </div>
  )
}

function UserCard({ user, onClick }: { user: UserRow; onClick: () => void }) {
  const today = new Date().toISOString().slice(0, 10)
  const usedToday = user.dream_used_today === today
  const activeUnlocks = (user.unlocked_gurus ?? []).filter(g => new Date(g.expires_at) > new Date()).length

  return (
    <button onClick={onClick}
            className="w-full flex items-center gap-3 p-3 md:p-4 text-left transition-colors hover:bg-black/[0.02] border-b last:border-0"
            style={{ borderColor: 'rgba(0,0,0,0.04)' }}>
      <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0"
           style={{ background: 'linear-gradient(135deg, #d4b884 0%, #b8945a 100%)' }}>
        {user.avatar_url ? (
          <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-lg">🪬</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="font-semibold text-sm truncate" style={{ color: '#2d2820' }}>
            {user.display_name ?? '(ไม่มีชื่อ)'}
          </span>
          {user.role === 'super_admin' && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                  style={{ background: '#b8945a', color: '#fff' }}>
              <Crown size={9} /> SUPER
            </span>
          )}
          {user.role === 'admin' && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                  style={{ background: '#7a8a6a', color: '#fff' }}>
              ADMIN
            </span>
          )}
        </div>
        <div className="text-[11px] font-mono truncate" style={{ color: '#8a8270' }}>
          {user.line_uid.slice(0, 16)}...
        </div>
        <div className="text-[10px] mt-0.5" style={{ color: '#a89e88' }}>
          สมาชิก {formatDate(user.created_at)}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        {usedToday && (
          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded"
                style={{ background: '#f0e0e8', color: '#9a7a8e' }}>
            ใช้แล้ววันนี้
          </span>
        )}
        {activeUnlocks > 0 && (
          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded"
                style={{ background: '#f5e8d0', color: '#b8945a' }}>
            🎫 {activeUnlocks}
          </span>
        )}
      </div>
    </button>
  )
}

function UserDetailModal({ user, onClose, onUnlock, unlocking }: {
  user: UserRow
  onClose: () => void
  onUnlock: (count: number, reason?: string) => void
  unlocking: boolean
}) {
  const { lineUid } = useAppStore()
  const [unlockCount, setUnlockCount] = useState(1)
  const [reason, setReason] = useState('')

  const { data: detail } = useQuery({
    queryKey: ['admin', 'user-detail', user.id],
    queryFn: () => adminApi.userDetail(lineUid!, user.id),
    enabled: !!lineUid,
  }) as { data: { stats: { total_dreams: number; total_transactions: number; active_tickets: number } } | undefined }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: 'rgba(0,0,0,0.5)' }}
         onClick={onClose}>
      <div className="w-full max-w-md rounded-3xl p-6 max-h-[90vh] overflow-y-auto"
           style={{ background: '#faf7ef' }}
           onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full overflow-hidden flex items-center justify-center"
                 style={{ background: 'linear-gradient(135deg, #d4b884 0%, #b8945a 100%)' }}>
              {user.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : <span className="text-xl">🪬</span>}
            </div>
            <div>
              <div className="font-bold text-base" style={{ color: '#2d2820' }}>
                {user.display_name ?? '(ไม่มีชื่อ)'}
              </div>
              <div className="text-[10px] font-mono" style={{ color: '#8a8270' }}>
                {user.line_uid}
              </div>
            </div>
          </div>
          <button onClick={onClose}
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(0,0,0,0.05)' }}>
            <X size={16} style={{ color: '#6b6354' }} />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 mb-5">
          <Stat label="ทำนายฝัน" value={detail?.stats?.total_dreams ?? 0} />
          <Stat label="ธุรกรรม" value={detail?.stats?.total_transactions ?? 0} />
          <Stat label="ตั๋วคงเหลือ" value={detail?.stats?.active_tickets ?? 0} highlight />
        </div>

        {/* Manual Unlock */}
        <div className="rounded-2xl p-4 mb-3"
             style={{ background: '#f5e8d0', border: '1px solid #d4b884' }}>
          <div className="flex items-center gap-2 mb-3">
            <Gift size={16} style={{ color: '#8a6e40' }} />
            <h3 className="font-bold text-sm" style={{ color: '#8a6e40' }}>
              มอบสิทธิ์ทำนายฝัน (Manual Grant)
            </h3>
          </div>

          <div className="mb-3">
            <label className="text-[11px] font-semibold block mb-1.5" style={{ color: '#6b6354' }}>
              จำนวนตั๋ว
            </label>
            <div className="flex gap-2">
              {[1, 3, 5, 10].map(n => (
                <button key={n}
                        onClick={() => setUnlockCount(n)}
                        className="flex-1 py-2 rounded-lg text-sm font-bold transition-colors"
                        style={{
                          background: unlockCount === n ? '#b8945a' : '#fff',
                          color: unlockCount === n ? '#fff' : '#2d2820',
                          border: '1px solid #d4b884',
                        }}>
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-3">
            <label className="text-[11px] font-semibold block mb-1.5" style={{ color: '#6b6354' }}>
              เหตุผล (optional)
            </label>
            <input type="text"
                   value={reason}
                   onChange={e => setReason(e.target.value)}
                   placeholder="เช่น ระบบเก็บเงิน Bug, ขอขมา"
                   className="w-full px-3 py-2 rounded-lg text-xs outline-none"
                   style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', color: '#2d2820' }} />
          </div>

          <button onClick={() => onUnlock(unlockCount, reason || undefined)}
                  disabled={unlocking}
                  className="w-full py-2.5 rounded-xl font-bold text-sm disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(135deg, #d4b884 0%, #b8945a 100%)',
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(184, 148, 90, 0.3)',
                  }}>
            {unlocking ? 'กำลังมอบสิทธิ์...' : `🎁 มอบ ${unlockCount} ตั๋ว`}
          </button>
        </div>

        <p className="text-[10px] text-center" style={{ color: '#8a8270' }}>
          ระบบจะส่ง LINE notification ให้ user อัตโนมัติ
        </p>
      </div>
    </div>
  )
}

function Stat({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className="text-center p-2 rounded-xl"
         style={{ background: highlight ? '#f5e8d0' : 'rgba(0,0,0,0.03)' }}>
      <div className="text-lg font-bold" style={{ color: highlight ? '#8a6e40' : '#2d2820' }}>{value}</div>
      <div className="text-[10px]" style={{ color: '#6b6354' }}>{label}</div>
    </div>
  )
}
